import { ref, Ref } from "vue";

export const useTableRowDragAndDrop = <T extends { id: string; order: number }>(
  data: Ref<T[]>,
  onReorder: (reorderedData: T[]) => void
) => {
  const draggedId = ref<string | null>(null);
  const dragOverId = ref<string | null>(null);

  const handleDragStart = (e: DragEvent, rowId: string, rowLabel: string) => {
    draggedId.value = rowId;

    // Create custom drag image showing the column name
    const dragImage = document.createElement("div");
    dragImage.style.cssText = `
      position: absolute;
      top: -9999px;
      left: -9999px;
      padding: 12px 20px;
      background: var(--mc-color-primary, #003d82);
      color: white;
      border-radius: 6px;
      font-weight: 500;
      font-size: 14px;
      white-space: nowrap;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      border: 2px solid var(--mc-color-primary, #003d82);
      z-index: 10000;
    `;
    dragImage.textContent = rowLabel;
    document.body.appendChild(dragImage);

    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setDragImage(dragImage, 0, 0);
    }

    // Clean up drag image after drag starts
    setTimeout(() => {
      if (document.body.contains(dragImage)) {
        dragImage.remove();
      }
    }, 0);
  };

  const handleDragEnd = () => {
    draggedId.value = null;
    dragOverId.value = null;
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = "move";
    }
  };

  const handleDragEnter = (rowId: string) => {
    if (draggedId.value && draggedId.value !== rowId && data.value.some((row) => row.id === draggedId.value)) {
      dragOverId.value = rowId;
    }
  };

  const handleDragLeave = () => {
    dragOverId.value = null;
  };

  const handleDrop = (e: DragEvent, targetId: string) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedId.value || draggedId.value === targetId) {
      draggedId.value = null;
      dragOverId.value = null;
      return;
    }

    const draggedIndex = data.value.findIndex((item) => item.id === draggedId.value);
    const targetIndex = data.value.findIndex((item) => item.id === targetId);

    if (draggedIndex === -1 || targetIndex === -1) {
      draggedId.value = null;
      dragOverId.value = null;
      return;
    }

    const reorderedData = data.value.map((item) => ({ ...item }));
    const draggedItem = reorderedData[draggedIndex];

    reorderedData.splice(draggedIndex, 1);
    reorderedData.splice(targetIndex, 0, draggedItem);

    for (const [idx, item] of reorderedData.entries()) {
      item.order = idx + 1;
    }

    data.value = reorderedData;
    draggedId.value = null;
    dragOverId.value = null;

    onReorder(reorderedData);
  };

  const handleOrderChange = (event: Event, rowId: string, data: Ref<T[]>) => {
    const input = event.target as HTMLInputElement;
    const newOrderNum = Number.parseInt(input.value, 10);
    const totalRows = data.value.length;

    if (Number.isNaN(newOrderNum) || newOrderNum < 1 || newOrderNum > totalRows) {
      return;
    }

    const rowIndex = data.value.findIndex((col) => col.id === rowId);
    if (rowIndex === -1) return;

    const currentOrder = data.value[rowIndex].order;
    if (currentOrder === newOrderNum) return;

    const reorderedData = data.value.map((item) => ({ ...item }));
    const movedItem = reorderedData[rowIndex];

    reorderedData.splice(rowIndex, 1);
    reorderedData.splice(newOrderNum - 1, 0, movedItem);

    for (const [idx, col] of reorderedData.entries()) {
      col.order = idx + 1;
    }

    data.value = reorderedData;
    onReorder(reorderedData);
  };

  return {
    draggedId,
    dragOverId,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    handleOrderChange
  };
};
